import IntegrationService from "@/services/integration";
import { i18n } from "@/boot/i18n";
import ErrorHandler from "@/tools/errorHandler";
import * as types from "./mutationTypes";
import { validate } from "../validator/validators";
import { REQUIRED, URL } from "../validator/methods";
import { INTEGRATIONS } from "../../../consts/integrations";

export default {
  clearState({ commit, dispatch }) {
    commit(types.CLEAR_INTEGRATIONS);
    commit(types.CLEAR_INTEGRATION);
    commit(types.CLEAR_INTEGRATION_ID);
    commit(types.CLEAR_IS_TOKEN_VALID);
    commit(types.CLEAR_GROUPS);
    dispatch("validator/clearValidator", null, { root: true });
  },
  initIntegrationWhatsapp({ commit }) {
    commit(types.SET_INTEGRATION, {
      integration: {
        enabled: false,
        name: "",
        phone: "",
        broker: 1,
        token: "",
        sender_key: "",
        namespace: "",
        workspace_id: "",
        webhook_url: ""
      }
    });
  },
  initIntegrationZendesk({ commit }) {
    commit(types.SET_INTEGRATION, {
      integration: {
        enabled: false,
        name: "",
        default: false,
        subdomain: "",
        token: "",
        email: "",
        send_attributes: false,
        health_score: {
          positive: {
            type: 1,
            priority: 1,
            status: 1,
            group: null,
            enabled: false
          },
          neutral: {
            type: 1,
            priority: 1,
            status: 1,
            group: null,
            enabled: false
          },
          negative: {
            type: 1,
            priority: 1,
            status: 1,
            group: null,
            enabled: false
          }
        }
      }
    });
  },
  clearIntegrations({ commit }) {
    commit(types.CLEAR_INTEGRATIONS);
  },
  setIntegrationId({ commit }, integrationId) {
    commit(types.SET_INTEGRATION_ID, { integrationId });
  },
  setToken({ commit }, token) {
    commit(types.SET_TOKEN, { token });
  },
  toggleIntegration({ commit }, enable) {
    commit(types.TOGGLE_INTEGRATION, { enable });
  },
  setUrl({ commit }, url) {
    commit(types.SET_URL, { url });
  },
  setName({ commit }, name) {
    commit(types.SET_NAME, { name });
  },
  setPhone({ commit }, phone) {
    commit(types.SET_PHONE, { phone });
  },
  setSenderKey({ commit }, senderKey) {
    commit(types.SET_SENDER_KEY, { senderKey });
  },
  setNamespace({ commit }, namespace) {
    commit(types.SET_NAMESPACE, { namespace });
  },
  setWorkspaceId({ commit }, workspaceId) {
    commit(types.SET_WORKSPACE_ID, { workspaceId });
  },
  setWebhookUrl({ commit }, webhookUrl) {
    commit(types.SET_WEBHOOK_URL, { webhookUrl });
  },
  setBroker({ commit }, broker) {
    commit(types.SET_BROKER, { broker });
  },
  setDefault({ commit }, isDefault) {
    commit(types.SET_DEFAULT, { isDefault });
  },
  setSubdomain({ commit }, subdomain) {
    commit(types.SET_SUBDOMAIN, { subdomain });
  },
  setEmail({ commit }, email) {
    commit(types.SET_EMAIL, { email });
  },
  setIsTokenValid({ commit }, valid) {
    commit(types.SET_IS_TOKEN_VALID, { valid });
  },
  setGroups({ commit }, groups) {
    commit(types.SET_GROUPS, { groups });
  },
  setSendAttributes({ commit }, sendAttributes) {
    commit(types.SET_SEND_ATTRIBUTES, { sendAttributes });
  },
  setEmailInviteTemplateUuid({ commit }, templateUuid) {
    commit(types.SET_EMAIL_INVITE_TEMPLATE_UUID, { templateUuid });
  },
  setEmailReminderTemplateUuid({ commit }, templateUuid) {
    commit(types.SET_EMAIL_REMINDER_TEMPLATE_UUID, { templateUuid });
  },
  setLinkSmsInviteTemplateUuid({ commit }, templateUuid) {
    commit(types.SET_LINKSMS_INVITE_TEMPLATE_UUID, { templateUuid });
  },
  setLinkSmsReminderTemplateUuid({ commit }, templateUuid) {
    commit(types.SET_LINKSMS_REMINDER_TEMPLATE_UUID, { templateUuid });
  },
  setThanksTemplateUuid({ commit }, templateUuid) {
    commit(types.SET_THANKS_TEMPLATE_UUID, { templateUuid });
  },
  setExpiredTemplateUuid({ commit }, templateUuid) {
    commit(types.SET_EXPIRED_TEMPLATE_UUID, { templateUuid });
  },
  setHealthScorePositiveGroup({ commit }, group) {
    commit(types.SET_HEALTH_SCORE_POSITIVE_GROUP, { group });
  },
  setHealthScoreNegativeGroup({ commit }, group) {
    commit(types.SET_HEALTH_SCORE_NEGATIVE_GROUP, { group });
  },
  setHealthScoreNeutralGroup({ commit }, group) {
    commit(types.SET_HEALTH_SCORE_NEUTRAL_GROUP, { group });
  },
  setHealthScorePositiveType({ commit }, type) {
    commit(types.SET_HEALTH_SCORE_POSITIVE_TYPE, { type });
  },
  setHealthScoreNegativeType({ commit }, type) {
    commit(types.SET_HEALTH_SCORE_NEGATIVE_TYPE, { type });
  },
  setHealthScoreNeutralType({ commit }, type) {
    commit(types.SET_HEALTH_SCORE_NEUTRAL_TYPE, { type });
  },
  setHealthScorePositivePriority({ commit }, priority) {
    commit(types.SET_HEALTH_SCORE_POSITIVE_PRIORITY, { priority });
  },
  setHealthScoreNegativePriority({ commit }, priority) {
    commit(types.SET_HEALTH_SCORE_NEGATIVE_PRIORITY, { priority });
  },
  setHealthScoreNeutralPriority({ commit }, priority) {
    commit(types.SET_HEALTH_SCORE_NEUTRAL_PRIORITY, { priority });
  },
  setHealthScorePositiveStatus({ commit }, status) {
    commit(types.SET_HEALTH_SCORE_POSITIVE_STATUS, { status });
  },
  setHealthScoreNegativeStatus({ commit }, status) {
    commit(types.SET_HEALTH_SCORE_NEGATIVE_STATUS, { status });
  },
  setHealthScoreNeutralStatus({ commit }, status) {
    commit(types.SET_HEALTH_SCORE_NEUTRAL_STATUS, { status });
  },
  setHealthScorePositiveEnabled({ commit }, enabled) {
    commit(types.SET_HEALTH_SCORE_POSITIVE_ENABLED, { enabled });
  },
  setHealthScoreNegativeEnabled({ commit }, enabled) {
    commit(types.SET_HEALTH_SCORE_NEGATIVE_ENABLED, { enabled });
  },
  setHealthScoreNeutralEnabled({ commit }, enabled) {
    commit(types.SET_HEALTH_SCORE_NEUTRAL_ENABLED, { enabled });
  },
  setWhatsappTemplates({ commit }, templates) {
    commit(types.SET_WHATSAPP_TEMPLATES, { templates });
  },
  clearWhatsappTemplates({ commit }) {
    commit(types.CLEAR_WHATSAPP_TEMPLATES);
  },
  async getAll({ dispatch, commit, rootState }) {
    try {
      const { data: responseData } = (await IntegrationService.getAll(rootState.organization.organization.uuid)) || {};
      const { data } = responseData || {};
      const integrations = data || [];
      commit(types.SET_INTEGRATIONS, { integrations });
    } catch (error) {
      if (error.response.status !== 403) {
        dispatch("ui/showToastError", ErrorHandler.getErrorMessage(error), { root: true });
      }
    }
  },
  async getAllAvailable({ dispatch, commit, rootState }) {
    try {
      const { data: responseData } =
        (await IntegrationService.getAvailable(rootState.organization.organization.uuid)) || {};
      const { data } = responseData || {};
      const integrations = data || [];
      commit(types.SET_INTEGRATIONS, { integrations });
    } catch (error) {
      dispatch("ui/showToastError", ErrorHandler.getErrorMessage(error), { root: true });
    }
  },
  async getOne({ dispatch, commit, rootState, state }, uuid) {
    try {
      const integrationID = state.integrationId;
      const { data: responseData } =
        (await IntegrationService.getOne(rootState.organization.organization.uuid, integrationID, uuid)) || {};
      const { data } = responseData || {};
      const integration = data || {};
      commit(types.SET_INTEGRATION, { integration });
    } catch (error) {
      dispatch("ui/showToastError", ErrorHandler.getErrorMessage(error), { root: true });
    }
  },
  async apiGenerateNewToken({ dispatch, commit, rootState }) {
    try {
      const { data: responseData } =
        (await IntegrationService.apiGenerateNewToken(rootState.organization.organization.uuid)) || {};
      const { data } = responseData || {};
      const integration = data || {};
      commit(types.SET_INTEGRATION, { integration });
      dispatch("ui/showToastSuccess", i18n.t("integration.api.generate_new_success"), { root: true });
    } catch (error) {
      dispatch("ui/showToastError", ErrorHandler.getErrorMessage(error), { root: true });
    }
  },
  async update({ dispatch, commit, rootState, state }, uuid) {
    try {
      const integrationID = state.integrationId;
      const { data: responseData } =
        (await IntegrationService.update(
          rootState.organization.organization.uuid,
          integrationID,
          state.integration,
          uuid
        )) || {};
      const { data } = responseData || {};
      const integration = data || {};
      commit(types.SET_INTEGRATION, { integration });
      dispatch("ui/showToastSuccess", i18n.t("integration.messages.update_success"), { root: true });
    } catch (error) {
      dispatch("ui/showToastError", ErrorHandler.getErrorMessage(error), { root: true });
    }
  },
  async create({ dispatch, commit, rootState, state }) {
    try {
      const integrationID = state.integrationId;
      const { data: responseData } =
        (await IntegrationService.create(rootState.organization.organization.uuid, integrationID, state.integration)) ||
        {};
      const { data } = responseData || {};
      const integration = data || {};
      commit(types.SET_INTEGRATION, { integration });
      dispatch("ui/showToastSuccess", i18n.t("integration.messages.update_success"), { root: true });
    } catch (error) {
      dispatch("ui/showToastError", ErrorHandler.getErrorMessage(error), { root: true });
    }
  },
  async zendeskVerifyToken({ commit, dispatch, rootState, state }) {
    try {
      const subdomain = state.integration.subdomain;
      const token = state.integration.token;
      const email = state.integration.email;
      const { data: responseData } = await IntegrationService.zendeskVerifyToken(
        rootState.organization.organization.uuid,
        subdomain,
        token,
        email
      );
      const { data } = responseData || {};
      const groups = data.groups || {};
      if (groups.length > 0) {
        commit(types.SET_IS_TOKEN_VALID, { valid: groups.length > 0 });
        commit(types.SET_GROUPS, { groups: groups.map((g) => ({ value: g.id, label: g.name })) });
        dispatch("setHealthScorePositiveGroup", groups[0].id);
        dispatch("setHealthScoreNeutralGroup", groups[0].id);
        dispatch("setHealthScoreNegativeGroup", groups[0].id);
      } else {
        commit(types.SET_IS_TOKEN_VALID, false);
      }
    } catch (error) {
      commit(types.SET_IS_TOKEN_VALID, false);
      dispatch("ui/showToastError", i18n.t("integration.zendesk.validation.token_request_error"), { root: true });
    }
  },
  async getWhatsappTemplates({ dispatch, commit, rootState }, integrationUuid) {
    try {
      const { data: responseData } =
        (await IntegrationService.getWhatsappTemplates(rootState.organization.organization.uuid, integrationUuid)) ||
        {};
      const { data } = responseData || {};
      const templates = data.templates || {};
      commit(types.SET_WHATSAPP_TEMPLATES, { templates });
    } catch (error) {
      dispatch("ui/showToastError", ErrorHandler.getErrorMessage(error), { root: true });
    }
  },
  async validateWebhook({ dispatch, state }) {
    if (state.integrationId !== INTEGRATIONS.WEBHOOK) {
      return true;
    }
    await dispatch("validator/removeError", "url", { root: true });
    if (!validate(REQUIRED, state.integration.url)) {
      dispatch(
        "validator/addError",
        {
          field: "url",
          message: i18n.t("general.rules.required")
        },
        { root: true }
      );
      return false;
    }
    if (!validate(URL, state.integration.url)) {
      dispatch(
        "validator/addError",
        {
          field: "url",
          message: i18n.t("general.rules.url")
        },
        { root: true }
      );
      return false;
    }
    return true;
  },
  async validateZendesk({ dispatch, state }) {
    if (!validate(REQUIRED, state.integration.name)) {
      dispatch(
        "validator/addError",
        {
          field: "name",
          message: i18n.t("integration.zendesk.validation.name_required")
        },
        { root: true }
      );
      return false;
    }
    if (!state.isTokenValid) {
      dispatch("ui/showToastError", i18n.t("integration.zendesk.validation.token_validation_required"), { root: true });
      return false;
    }
    if (!hasAtLeastOneHealthScore(state.integration)) {
      dispatch("ui/showToastError", i18n.t("integration.zendesk.validation.health_score_required"), { root: true });
      return false;
    }
    return true;
  }
};

function hasAtLeastOneHealthScore(integration) {
  const {
    health_score: { positive, neutral, negative }
  } = integration;
  return positive.enabled || neutral.enabled || negative.enabled;
}
